import { ActionAndOthers, ActionKind } from "client_legacy/ndaw/actions";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState,
	KeyboardEvent,
	useImperativeHandle,
	Ref
} from "react";
import { IconButton } from "../IconButton/IconButton";
import { Key } from "client/Misc/commonTypes";
import {
	KeyBoardListNavigationContext,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem
} from "client/Hooks";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { actionComponentFactory } from "../ActionList/actionsRenderHelpers";
import { ensureNotNil } from "client/Misc/utils";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";

export type GridItemMoreActionsHandle = {
	closeActions(): void;
};

type GridItemMoreActionsProps<TActionContext> = {
	isWhite?: boolean;
	menuTitle: string;
	actions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
	tabIndex: number;
	ref?: Ref<GridItemMoreActionsHandle>;
};

export const GridItemMoreActions = <TActionContext, >(
	props: GridItemMoreActionsProps<TActionContext>
): ReactElement => {
	const {
		isWhite = false,
		menuTitle,
		actions,
		actionContext,
		tabIndex,
		ref
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	useImperativeHandle(
		ref,
		(): GridItemMoreActionsHandle => ({
			closeActions(): void {
				setIsOpen(false);
			},
		}),
		[]
	);

	const focusMenuButtonHandler = useCallback(
		(): void => {
			menuButtonRef.current
				?.focus();
		},
		[]
	);

	const closeMenuHandler = useCallback(
		(): void => {
			setIsOpen(false);
			focusMenuButtonHandler();
		},
		[focusMenuButtonHandler]
	);

	const {
		contextData,
		focusItemAtIndexHandler
	} = useKeyboardListNavigation({
		onFocusInput: focusMenuButtonHandler,
		onCancel: closeMenuHandler,
		shouldCancelDirectlyFromItems: true
	});

	const menuButtonKeyDownHandler = useCallback(
		(event: KeyboardEvent): void => {
			const key: Key | null = tryResolveKeyboardKey(event);

			switch (key) {
				case Key.Enter:
				case Key.Space:
					setIsOpen(true);

					// Delay the focus to ensure the menu is mounted
					setTimeout(
						(): void => {
							focusItemAtIndexHandler(0);
						},
						0
					);

					event.preventDefault();
					return;
				case Key.Escape:
				case Key.Tab:
					if (isOpen) {
						closeMenuHandler();
					}

					return;
				default:
					return;
			}
		},
		[
			closeMenuHandler,
			focusItemAtIndexHandler,
			isOpen
		]
	);

	const renderActions = (): ReactElement | null => {
		let navigableActionIndex: number = -1;

		return (
			<>
				{isOpen && (
					<DropOverActionList
						trackingId="grid-item-action-list"
						target={ensureNotNil(menuButtonRef.current)}
						targetAlignment="bl"
						contentAlignment="tl"
						onClose={closeMenuHandler}
						bubbleClick={false}
						bubbleContextMenu={false}
						menuTitle={menuTitle}
					>
						<KeyBoardListNavigationContext.Provider value={contextData}>
							{actions.map((x) => {
								if (x.kind === ActionKind.Divider || x.kind === ActionKind.BusyIndicator) {
									return actionComponentFactory(
										x,
										actionContext
									);
								}

								navigableActionIndex++;

								return (
									<NavigableActionItem
										key={x.name}
										action={x}
										actionContext={actionContext}
										index={navigableActionIndex}
										onCloseMenu={closeMenuHandler}
									/>
								);
							})}
						</KeyBoardListNavigationContext.Provider>
					</DropOverActionList>
				)}
			</>
		);
	};

	return (
		<>
			<IconButton
				trackingId="more"
				icon={{
					kind: "font",
					name: "ellipsis",
					color: isWhite
						? "white"
						: undefined
				}}
				kind="transparent-colored"
				onClick={() => setIsOpen(true)}
				ref={menuButtonRef}
				bubbleClick={false}
				shape="round"
				isActive={isOpen}
				onKeyDown={menuButtonKeyDownHandler}
				tabIndex={tabIndex}
			/>
			{renderActions()}
		</>
	);
};

type NavigableActionItemProps<TActionContext> = {
	action: ActionAndOthers<TActionContext>;
	actionContext: TActionContext;
	index: number;
	onCloseMenu: () => void;
};

const NavigableActionItem = <TActionContext, >(
	props: NavigableActionItemProps<TActionContext>
): ReactElement => {
	const {
		action,
		actionContext,
		index,
		onCloseMenu
	} = props;

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: (): void => {
			if (action.canExecute?.(actionContext)) {
				action.execute?.(actionContext);
				onCloseMenu();
			}
		},
		executeKey: [
			Key.Enter,
			Key.Space
		],
		shouldCancelOnTab: true
	});

	const KeyDownHandler = useCallback(
		(args: KeyboardEvent<HTMLLIElement>): void => {
			onKeyDown(args);
			args.stopPropagation();
		},
		[onKeyDown]
	);

	return actionComponentFactory(
		action,
		actionContext,
		{
			ref: ref,
			onKeyDown: KeyDownHandler,
			tabIndex: -1
		}
	);
};
NavigableActionItem.displayName = "NavigableActionItem";
